import { useRef, useState, useEffect } from 'react';

const STEPS = [
  {
    num: '01',
    icon: 'ri-search-eye-line',
    title: 'Discovery',
    desc: 'We assess your parts types, production volumes, contamination levels, and workflow to fully understand your operation before making a single recommendation.',
  },
  {
    num: '02',
    icon: 'ri-file-chart-line',
    title: 'System Assessment',
    desc: 'Our engineers evaluate your facility, compliance requirements, and throughput targets to define the optimal system configuration for your environment.',
  },
  {
    num: '03',
    icon: 'ri-pencil-ruler-2-line',
    title: 'Custom Design',
    desc: 'Every system is engineered specifically around your operation — no off-the-shelf compromise. Dimensions, cycles, temperatures, and capacity are all spec-built.',
  },
  {
    num: '04',
    icon: 'ri-hammer-line',
    title: 'Build &amp; Install',
    desc: 'Your MAXJET system is built in the USA and installed by our experienced team, ensuring seamless integration with minimal disruption to your production schedule.',
  },
  {
    num: '05',
    icon: 'ri-test-tube-line',
    title: 'Validation',
    desc: 'Rigorous testing confirms every component performs to specification. Cycle times, throughput, cleaning effectiveness, and safety standards are all validated on-site.',
  },
  {
    num: '06',
    icon: 'ri-shield-user-line',
    title: 'Ongoing Support',
    desc: 'Direct access to our team for any questions, parts, or service needs — long after installation. We stand behind every system we build.',
  },
];

export default function ProcessSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="relative bg-[#0a0a0a] py-32 overflow-hidden" ref={ref}>

      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.8) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      {/* Decorative large ghosted number */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 font-serif text-[20rem] text-[#c9a84c]/3 font-bold leading-none select-none pointer-events-none pr-8">
        06
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12">

        {/* Section header */}
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <span className="inline-flex items-center gap-3 text-[#c9a84c] text-[10px] font-bold tracking-[0.5em] uppercase font-sans">
              <span className="h-px w-8 bg-[#c9a84c]" />
              Our Process
            </span>
            <h2 className="font-serif text-5xl lg:text-6xl text-white mt-5 leading-tight">
              A Structured Approach to<br />
              Precision and Performance
            </h2>
          </div>
          <p className="text-white/35 font-sans text-base leading-relaxed max-w-sm lg:text-right">
            Every project follows a proven six-step process. Your system is built right, installed right, and performing from day one.
          </p>
        </div>

        {/* Horizontal step diagram — row 1 (steps 1-3) */}
        <div className={`relative mb-0 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>

          {/* Row 1 */}
          <div className="grid grid-cols-3 gap-0 relative mb-0">
            {/* Connecting line across row 1 */}
            <div className="absolute top-[2.75rem] left-[calc(16.5%)] right-[calc(16.5%)] h-px bg-gradient-to-r from-[#c9a84c]/20 via-[#c9a84c]/40 to-[#c9a84c]/20 hidden lg:block" />

            {STEPS.slice(0, 3).map((step, idx) => (
              <div
                key={idx}
                className={`relative group flex flex-col items-center text-center px-8 pb-0 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${200 + idx * 100}ms` }}
              >
                {/* Step indicator */}
                <div className="relative mb-6 z-10">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0d0d0d] border-2 border-[#c9a84c]/20 group-hover:border-[#c9a84c]/60 transition-all duration-400 group-hover:bg-[#c9a84c]/8">
                    <i className={`${step.icon} text-[#c9a84c] text-xl`} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#c9a84c] text-black text-[9px] font-bold font-sans">
                    {step.num}
                  </div>
                </div>

                {/* Card */}
                <div className="w-full bg-[#0d0d0d] border border-[#1c1c1c] group-hover:border-[#c9a84c]/20 rounded-xl p-7 transition-all duration-400 group-hover:bg-[#111]">
                  <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#c9a84c]/90 transition-colors">
                    <span dangerouslySetInnerHTML={{ __html: step.title }} />
                  </h3>
                  <p className="text-white/40 text-sm font-sans leading-relaxed">{step.desc}</p>
                  <div className="mt-4 w-8 h-0.5 bg-[#c9a84c]/25 group-hover:w-14 group-hover:bg-[#c9a84c]/50 transition-all duration-400 mx-auto" />
                </div>
              </div>
            ))}
          </div>

          {/* Descending connector */}
          <div className="hidden lg:flex justify-end pr-[16.5%] py-4">
            <div className="flex items-center gap-2 text-[#c9a84c]/25">
              <div className="w-px h-8 bg-[#c9a84c]/20" />
              <i className="ri-arrow-down-line text-xs" />
            </div>
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-3 gap-0 relative">
            {/* Connecting line across row 2 — reversed direction */}
            <div className="absolute top-[2.75rem] left-[calc(16.5%)] right-[calc(16.5%)] h-px bg-gradient-to-l from-[#c9a84c]/20 via-[#c9a84c]/40 to-[#c9a84c]/20 hidden lg:block" />

            {[...STEPS.slice(3)].reverse().map((step, idx) => {
              const realIdx = 5 - idx;
              return (
                <div
                  key={realIdx}
                  className={`relative group flex flex-col items-center text-center px-8 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${500 + idx * 100}ms` }}
                >
                  <div className="relative mb-6 z-10">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0d0d0d] border-2 border-[#c9a84c]/30 group-hover:border-[#c9a84c]/70 transition-all duration-400 group-hover:bg-[#c9a84c]/8">
                      <i className={`${step.icon} text-[#c9a84c] text-xl`} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-[#c9a84c] text-black text-[9px] font-bold font-sans">
                      {step.num}
                    </div>
                  </div>
                  <div className="w-full bg-[#0d0d0d] border border-[#1c1c1c] group-hover:border-[#c9a84c]/20 rounded-xl p-7 transition-all duration-400 group-hover:bg-[#111]">
                    <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#c9a84c]/90 transition-colors">
                      <span dangerouslySetInnerHTML={{ __html: step.title }} />
                    </h3>
                    <p className="text-white/40 text-sm font-sans leading-relaxed">{step.desc}</p>
                    <div className="mt-4 w-8 h-0.5 bg-[#c9a84c]/25 group-hover:w-14 group-hover:bg-[#c9a84c]/50 transition-all duration-400 mx-auto" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Mid-page CTA */}
        <div className={`mt-16 transition-all duration-700 delay-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative rounded-xl overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/5e09f5847bbc51c922fad697a6ccceb2.jpg"
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-[#060606]/90" />
            </div>
            <div className="relative z-10 border border-[#1c1c1c] rounded-xl px-10 py-9 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <p className="text-[#c9a84c] text-[10px] font-bold tracking-[0.4em] uppercase font-sans mb-2">Ready to Begin?</p>
                <h3 className="font-serif text-2xl text-white">Let&apos;s Start Your System Project</h3>
                <p className="text-white/35 text-sm font-sans mt-2">Our engineers respond the same business day to all inquiries.</p>
              </div>
              <div className="flex flex-wrap gap-4 flex-shrink-0">
                <a
                  href="#contact"
                  className="px-8 py-4 bg-[#c9a84c] text-black font-bold text-sm rounded-md hover:bg-[#d4b55a] transition-colors cursor-pointer font-sans whitespace-nowrap"
                >
                  Start Your Project
                </a>
                <a
                  href="tel:8665407575"
                  className="px-8 py-4 border border-[#c9a84c]/25 text-[#c9a84c] font-semibold text-sm rounded-md hover:border-[#c9a84c] hover:bg-[#c9a84c]/5 transition-colors cursor-pointer font-sans whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-phone-line" /> 866-540-7575
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
